import React, { useMemo, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import {
  DocumentCoreFields,
  DocumentCoreFieldsInitialValues,
  documentFieldsValidation,
  UploadMultipleMedia,
} from '~/components/Documents/DocumentCard';
import { DocumentsTable, getDocumentsTableColumnData } from '~/components/Documents/DocumentsContainer';
import { getAllowedDocumentTypes } from '~/components/Documents/utils/documentTypes';
import { useCms } from '~/components/hooks/useCms';
import { useIncidentConfiguration } from '~/components/hooks/useIncidentConfiguration';
import { useLob } from '~/components/hooks/useLob';
import useOrganization from '~/components/OrganizationContext';
import type { StoredFile } from '~/components/types/claim-document-types';
import type { OrganizationDocumentType } from '~/components/types/document-type';
import type { OrganizationModel } from '~/components/types/organization-types';

import { PencilIcon, TrashIcon } from '../../../icons';
import InlineIconButton from '../../../InlineIconButton';

// Define DocumentMetaData type based on server/api/schemas/schemas.py DocumentParam
interface DocumentMetaData {
  document_extension?: string;
  document_url?: string;
  stored_file_id: number; // specific for FNOL via FE
  external_id: string;
  document_date?: string;
  summary?: string;
  document_name: string;
  document_date_received?: string;
  document_type: string;
  exposure_numbers?: string[];
  storage_file_uuid?: string;
}

interface FnolFilesFormikValues {
  documents: DocumentMetaData[];
}

export interface FnolDocumentTableItem {
  id: number;
  document_name: string;
  type: string;
  summary: string;
  document_date: string;
  date_received: string;
  download_url: string;
}

export interface TableColumn<T = unknown> {
  id: string;
  label: string;
  numeric?: boolean;
  disableSort?: boolean;
  specialCell?: (item: T, rowIndex?: number) => React.ReactNode;
}

// Helper function to map StoredFile to DocumentMetaData
const mapStoredFileToDocumentMetaData = (file: StoredFile): DocumentMetaData => {
  const getExtension = (filename: string | undefined): string | undefined => {
    if (!filename) return undefined;
    const parts = filename.split('.');
    return parts.length > 1 ? `.${parts.pop()}` : undefined;
  };

  // Ensure stored_file_extra is treated as potentially undefined or an object
  const extra =
    typeof file.stored_file_extra === 'object' && file.stored_file_extra !== null
      ? (file.stored_file_extra as Record<string, unknown>)
      : {};

  // Access properties safely from extra
  const document_type = (extra?.document_type as string | undefined) || 'other';
  const document_name = (extra?.document_name as string | undefined) || file.original_filename || 'Unnamed File';
  const document_date = extra?.document_date as string | undefined;
  const summary = extra?.summary as string | undefined;
  const document_date_received = (extra?.document_date_received as string | undefined) || file.datetime_uploaded;

  return {
    stored_file_id: file.id,
    external_id: String(file.id),
    document_name,
    document_type,
    document_extension: getExtension(file.original_filename),
    // Optional fields, use value from stored_file_extra if available, otherwise undefined
    document_date, // Use safely accessed variable
    summary, // Use safely accessed variable
    document_date_received,
    // Fields not available from StoredFile in this context
    document_url: undefined,
    exposure_numbers: undefined,
    storage_file_uuid: undefined,
  };
};

// New helper function to map DocumentMetaData to FnolDocumentTableItem
const mapDocumentMetaDataToFnolDocumentTableItem = (docParam: DocumentMetaData): FnolDocumentTableItem => ({
  // Not external_id, which holds the API caller's own reference for a document sent with a draft
  id: docParam.stored_file_id,
  document_name: docParam.document_name,
  type: docParam.document_type,
  summary: docParam.summary || '',
  document_date: docParam.document_date || '',
  date_received: docParam.document_date_received || '',
  download_url: `/api/v1/stored_files/${docParam.stored_file_id}/download`,
});

// A document sent with a draft through the external API can lack a document date, which the shared
// cell would fail to format.
const allowMissingDocumentDate = (column: TableColumn<FnolDocumentTableItem>): TableColumn<FnolDocumentTableItem> => {
  const { specialCell } = column;
  if (column.id !== 'document_date' || !specialCell) {
    return column;
  }
  return { ...column, specialCell: (item, rowIndex) => (item.document_date ? specialCell(item, rowIndex) : null) };
};

interface UploadContainerProps {
  label: string;
  mediaType: string;
}

const UploadContainer: React.FC<UploadContainerProps> = ({ label, mediaType }) => {
  const { setFieldValue, values } = useFormikContext<FnolFilesFormikValues>();
  const { classes } = useStyles();
  const { lob } = useLob();
  const { userOrganization } = useCms();
  const [showUploadFiles, setShowUploadFiles] = useState(false);

  return (
    <>
      <Grid item xs={4}>
        <Button color="primary" onClick={() => setShowUploadFiles(true)}>
          <CloudUploadIcon className={classes.leftButtonIcon} />
          {label}
        </Button>
      </Grid>
      {showUploadFiles && (
        <UploadMultipleMedia
          onClose={() => setShowUploadFiles(false)}
          alternativeUploadUrl={`/api/v1/organizations/${userOrganization.id}/stored_files`}
          mediaType={mediaType}
          onSubmit={(files: StoredFile[]) => {
            const mappedDocuments = files.map((file) => mapStoredFileToDocumentMetaData(file));
            const currentDocuments = Array.isArray(values.documents) ? values.documents : [];
            setFieldValue('documents', [...currentDocuments, ...mappedDocuments]);

            setShowUploadFiles(false);
          }}
          lob={lob}
          uploadOutsideOfClaim
        />
      )}
    </>
  );
};

interface FnolFilesDisplayTableProps {
  onEdit: (file: FnolDocumentTableItem) => void;
}

const FnolFilesDisplayTable: React.FC<FnolFilesDisplayTableProps> = ({ onEdit }) => {
  const { values, setFieldValue } = useFormikContext<FnolFilesFormikValues>();
  const { userOrganization } = useCms();
  const { documentTypesDict } = useOrganization() as {
    documentTypesDict: { [document_type: string]: OrganizationDocumentType };
  };

  // Create mapped files directly from values.documents using the new mapping function
  const mappedFnolFiles = useMemo(() => {
    const docs = Array.isArray(values.documents) ? values.documents : [];
    return docs.map(mapDocumentMetaDataToFnolDocumentTableItem);
  }, [values.documents]);

  const handleRemoveFile = (fileToRemoveId: number) => {
    const currentDocuments = Array.isArray(values.documents) ? values.documents : [];
    setFieldValue(
      'documents',
      currentDocuments.filter((docParam) => docParam.stored_file_id !== fileToRemoveId)
    );
  };
  const getSharedColumns = (
    documentTypesDict: { [document_type: string]: OrganizationDocumentType },
    userOrganization: OrganizationModel
  ): TableColumn<FnolDocumentTableItem>[] => {
    const baseDocumentColumns = getDocumentsTableColumnData(documentTypesDict, userOrganization, false);
    const sharedColumnIds = ['type', 'document_name', 'summary', 'document_date', 'date_received'];
    const filteredBaseColumns = baseDocumentColumns.filter((col) => sharedColumnIds.includes(col.id));
    return (filteredBaseColumns as TableColumn<FnolDocumentTableItem>[]).map(allowMissingDocumentDate);
  };

  const sharedColumns = getSharedColumns(documentTypesDict, userOrganization);

  const fnolTableColumns: TableColumn<FnolDocumentTableItem>[] = [
    {
      id: 'id',
      label: '',
      numeric: false,
      disableSort: true,
      specialCell: () => null,
    },
    ...sharedColumns,
    {
      id: 'actions',
      label: 'Actions',
      disableSort: true,
      specialCell: (item: FnolDocumentTableItem) => (
        <div className="flex items-center justify-start">
          <InlineIconButton
            icon={PencilIcon}
            tooltipTitle="Edit"
            iconColor="currentColor"
            className="m-4 hover:text-teal-700"
            onClick={() => onEdit(item)}
          />
          <InlineIconButton
            icon={TrashIcon}
            iconColor="currentColor"
            tooltipTitle="Delete"
            className="m-4 hover:text-teal-700"
            onClick={() => handleRemoveFile(item.id)}
          />
        </div>
      ),
    },
  ];

  if (!mappedFnolFiles || mappedFnolFiles.length === 0) {
    return null;
  }

  return <DocumentsTable documents={mappedFnolFiles} tableColumns={fnolTableColumns} />;
};

interface EditFnolDocumentDialogProps {
  open: boolean;
  document: FnolDocumentTableItem | null;
  onClose: () => void;
  onSave: (updatedDocument: FnolDocumentTableItem) => void;
}

const EditFnolDocumentDialog: React.FC<EditFnolDocumentDialogProps> = ({ open, document, onClose, onSave }) => {
  const { classes } = useStyles();
  const { lob } = useLob();
  const { documentTypesDict } = useOrganization() as { documentTypesDict: Record<string, OrganizationDocumentType> };
  // There is no claim yet, so the filtering DocumentCoreFields does from one has to happen
  // here off the FNOL's lob — otherwise the dialog offers non-public and other-lob types.
  const allowedDocumentTypes = getAllowedDocumentTypes(documentTypesDict, lob);

  if (!open || !document) {
    return null;
  }

  // Prepare initial values for Formik, mapping from FnolDocumentTableItem
  const initialFormikValues = {
    ...DocumentCoreFieldsInitialValues,
    // A type the dialog cannot offer (one a draft was sent through the external API with) starts empty,
    // so the required check makes the adjuster pick one instead of saving the type the submit rejects.
    type: allowedDocumentTypes.includes(document.type) ? document.type : '',
    document_name: document.document_name || '',
    document_date: document.document_date || null,
    document_date_received: document.date_received || null,
    exposure_ids: [],
    summary: document.summary || '',
  };
  const validationSchema = Yup.object().shape({
    ...Object.fromEntries(
      Object.entries(documentFieldsValidation(false)).filter(([key]) => key !== 'file_size' && key !== 'exposure_ids')
    ),
  });

  return (
    <Formik
      initialValues={initialFormikValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        // Map Formik values back to FnolDocumentTableItem structure (or similar)
        // and call the original onSave prop
        const updatedTableItem: FnolDocumentTableItem = {
          ...document, // Keep original id, download_url etc.
          type: values.type,
          document_name: values.document_name,
          summary: values.summary,
          document_date: values.document_date || '',
          date_received: values.document_date_received || '', // *** CORRECTED ACCESS HERE ***
        };
        onSave(updatedTableItem);
      }}
      enableReinitialize // Important to reset form when document changes
    >
      {({ handleSubmit, isSubmitting }) => (
        <CardDialog
          open={open}
          isDialog
          maxWidth="sm"
          fullWidth
          onClose={onClose}
          title="Edit Document Details"
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              disabled={isSubmitting}
              onClickPrimary={handleSubmit}
              onClickSecondary={onClose}
              primaryLabel="Add"
            />
          }
        >
          <Grid container direction="column" spacing={2}>
            <DocumentCoreFields
              // Pass necessary props, adapting for FNOL context
              classes={classes} // Pass empty or relevant styles
              claim={null} // No claim object here
              allowedDocumentTypes={allowedDocumentTypes}
              hideExposuresLabels={true} // Exposures not managed here
              showOnly={false}
              disabled={isSubmitting}
            />
          </Grid>
        </CardDialog>
      )}
    </Formik>
  );
};

const FnolFiles: React.FC = () => {
  const { incidentConfiguration } = useIncidentConfiguration();
  const isDocumentUploadActive = incidentConfiguration?.documents?.active;
  const { values, setFieldValue } = useFormikContext<FnolFilesFormikValues>();

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<FnolDocumentTableItem | null>(null);

  const handleEditFile = (file: FnolDocumentTableItem) => {
    setEditingDocument(file);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingDocument(null);
  };

  const handleSaveEditDialog = (updatedDocumentFromDialog: FnolDocumentTableItem) => {
    const currentDocuments = Array.isArray(values.documents) ? values.documents : [];
    const updatedDocuments = currentDocuments.map((docParam) => {
      // The table item's id is the document's stored_file_id
      if (docParam.stored_file_id === updatedDocumentFromDialog.id) {
        // Map updated fields from FnolDocumentTableItem back to DocumentMetaData
        return {
          ...docParam,
          document_name: updatedDocumentFromDialog.document_name,
          document_type: updatedDocumentFromDialog.type,
          summary: updatedDocumentFromDialog.summary,
          document_date: updatedDocumentFromDialog.document_date,
          // Left unset rather than empty: the submit rejects an empty date and defaults a missing one
          document_date_received: updatedDocumentFromDialog.date_received || undefined,
          // Note: document_extension, stored_file_id, external_id remain unchanged
        };
      }
      return docParam;
    });
    setFieldValue('documents', updatedDocuments);
    handleCloseEditDialog();
  };

  if (!isDocumentUploadActive) {
    return null;
  }

  return (
    <>
      <CardDialog title="Files" containerStyle={{ borderRadius: '6px' }}>
        <Grid container direction="column" spacing={2}>
          <Grid item xs={12}>
            <Typography display="block" variant="body1" color="textSecondary">
              Add supporting documents, photos or videos
            </Typography>
          </Grid>
          <Grid container spacing={4}>
            <UploadContainer label="UPLOAD DOCUMENTS" mediaType="other" />
            <UploadContainer label="UPLOAD PHOTOS" mediaType="damage_photo" />
            <UploadContainer label="UPLOAD VIDEOS" mediaType="video" />
          </Grid>
          {values.documents && values.documents.length > 0 && (
            <Grid item xs={12}>
              <FnolFilesDisplayTable onEdit={handleEditFile} />
            </Grid>
          )}
          <Grid item xs={12} />
        </Grid>
      </CardDialog>
      {editingDocument && (
        <EditFnolDocumentDialog
          open={editDialogOpen}
          document={editingDocument}
          onClose={handleCloseEditDialog}
          onSave={handleSaveEditDialog}
        />
      )}
    </>
  );
};

export default FnolFiles;
